import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Switch,
  Tooltip,
} from "antd";
import GenericTable from "../../../components/Commons/Table/generic-table.component";
import { CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import ShowDataLength from "../../../components/Commons/Table/results-length-table.component";
import { useState } from "react";
import { getAll, remove, update } from "../../../services/services.service";
import { PaginationProps } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import { AlertService } from "../../../services/alert.service";
import { useMutation, useQuery } from "react-query";
import GenericDrawer from "../../../components/Commons/Drawer/generic-drawer.component";
import ServiceForm from "./Form";
import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";
import ProfilePermissionService from "../../../services/profilePermissions.service";
import { formatCurrency } from "../../../services/utils";
import { isMobile } from "react-device-detect";

const { Search } = Input;

export default function ServicesList() {
  const [services, setServices] = useState([]);
  const [currentPage, setCurrentPage] = useState<number | undefined>(1);
  const [perPage, setPerPage] = useState<number | undefined>(20);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<string>("name-asc");
  const [formSearch] = Form.useForm();
  const [openServiceDrawerForm, setOpenServiceDrawerForm] =
    useState<boolean>(false);
  const [serviceToEdit, setServiceToEdit] = useState<any>();
  const [leaveThePage, setLeaveThePage] = useState<boolean>(false);
  const [valuesChanged, setValuesChanged] = useState<boolean>(false);
  const [removeService, setRemoveService] = useState<any | undefined>(
    undefined
  );

  const profilePermissionService = ProfilePermissionService.getInstance();

  const { isLoading: serviceLoading, refetch: loadServices } = useQuery(
    ["loadServices", currentPage, search, order, perPage],
    () => getAll({ page: currentPage, per_page: perPage, order, search }),
    {
      onSuccess: (response) => {
        setServices(
          response.data.map((service: any) => {
            return {
              key: service.data.id,
              ...service.data,
            };
          })
        );

        setCurrentPage(response.meta.current_page);
        setPerPage(response.meta.per_page);
        setTotal(response.meta.total);
      },
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: removeServiceMutate } = useMutation(remove, {
    onSuccess: () => {
      AlertService.sendAlert([{ text: "Serviço apagado com sucesso." }]);

      setRemoveService(undefined);

      loadServices();
    },
  });

  const onSearch = (search_value: any) => {
    setCurrentPage(1);
    setSearch(search_value);
  };

  const handleTableChange = (
    pagination: PaginationProps,
    filters: Record<string, FilterValue | null>,
    sorter: any
  ) => {
    if (pagination.current != currentPage) {
      setCurrentPage(pagination.current);
    }

    if (pagination.pageSize != perPage) {
      setPerPage(pagination.pageSize);
      setCurrentPage(1);
    }

    if (sorter.order) {
      let orderBy = `${sorter.field}-${sorter.order.substr(
        0,
        sorter.order.length - 3
      )}`;

      if (order != orderBy) {
        setOrder(orderBy);
      }
    }
  };

  const onOpenServiceDrawerForm = (record: any) => {
    setServiceToEdit(record);
    setOpenServiceDrawerForm(true);
  };

  const onCloseServiceDrawerForm = () => {
    setOpenServiceDrawerForm(false);
    setServiceToEdit(undefined);
    setValuesChanged(false);
  };

  const onSavedForm = (successMessage: string) => {
    onCloseServiceDrawerForm();

    setTimeout(() => {
      AlertService.sendAlert([{ text: successMessage }]);

      loadServices();
    }, 500);
  };

  const onLeaveThePage = () => {
    if (!serviceToEdit || valuesChanged) {
      setLeaveThePage(true);
    } else {
      onCloseServiceDrawerForm();
    }
  };

  const { mutate: updateActiveStatusMutate } = useMutation(update, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Status do serviço atualizado com sucesso." },
      ]);
    },
  });

  const onChangeActiveStatus = (checked: boolean, id: number) => {
    const obj = {
      values: { active: checked },
      id,
    };

    updateActiveStatusMutate(obj);
  };

  const columns: any = [
    {
      title: "Serviço",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (_: any, record: any) => (
        <Badge color={record.color} text={record.name} />
      ),
    },
    {
      title: "Valor",
      dataIndex: "value",
      key: "value",
      sorter: true,
      render: (_: any, record: any) => formatCurrency(record.value),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ativo?",
      dataIndex: "active",
      key: "active",
      sorter: true,
      width: 150,
      render: (_: any, record: any) => {
        return (
          <Tooltip
            title={record.active ? "Desativar serviço" : "Ativar serviço"}
          >
            <Switch
              checked={record.active}
              onChange={(event: boolean) => {
                onChangeActiveStatus(event, record.id);
                record.active = event;
              }}
            />
          </Tooltip>
        );
      },
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ações",
      key: "action",
      render: (_: any, record: any) =>
        profilePermissionService.hasPermission("services-services:write") && (
          <Space size="small" direction={isMobile ? "vertical" : "horizontal"}>
            <Tooltip placement="top" title="Editar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<EditOutlined />}
                onClick={() => onOpenServiceDrawerForm(record)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Apagar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<DeleteOutlined />}
                onClick={() => setRemoveService(record)}
              />
            </Tooltip>
          </Space>
        ),
      width: "15%",
    },
  ];

  return (
    <>
      <Card
        styles={{
          body: { padding: 8 },
        }}
      >
        <Row>
          <Col
            lg={{ span: 12 }}
            md={{ span: 12 }}
            sm={{ span: 24 }}
            xs={{ span: 24 }}
          >
            <Form
              form={formSearch}
              name="search_services"
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item name="search_service">
                <Search
                  onSearch={onSearch}
                  placeholder="Pesquisa o serviço"
                  enterButton="Pesquisar"
                  allowClear
                  size="large"
                  maxLength={255}
                />
              </Form.Item>
            </Form>
          </Col>

          {profilePermissionService.hasPermission(
            "services-services:write"
          ) && (
            <Col
              lg={{ span: 4, offset: 8 }}
              md={{ span: 4, offset: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Button
                type="primary"
                onClick={() => onOpenServiceDrawerForm(undefined)}
                size="large"
                block
              >
                Criar novo
              </Button>
            </Col>
          )}
        </Row>

        <Divider />

        <div className="table">
          <GenericTable
            columns={columns}
            dataSource={services}
            pagination={{
              current: currentPage,
              pageSize: perPage,
              showSizeChanger: true,
              total: total,
            }}
            loading={serviceLoading}
            totalChildren={
              <div className="zone_result_size_n_export">
                <ShowDataLength size={total} className="show_result_size" />
              </div>
            }
            empty={{
              createTheFirst: !services.length && !search,
              textOne:
                "Esta tabela encontra-se vazia, uma vez que ainda não foram criados serviços.",
              textTwo: profilePermissionService.hasPermission(
                "services-services:write"
              )
                ? `Para começar, crie um novo serviço.`
                : "",
              link: profilePermissionService.hasPermission(
                "services-services:write"
              )
                ? () => onOpenServiceDrawerForm(undefined)
                : "",
            }}
            handleTableChange={handleTableChange}
          />
        </div>

        <GenericDrawer
          title={!serviceToEdit ? "Criar novo serviço" : "Editar serviço"}
          children={
            <>
              <ServiceForm
                service={serviceToEdit}
                closeForm={onLeaveThePage}
                savedForm={onSavedForm}
                unavailableColors={services.map(
                  (service: any) => service.color
                )}
                onValuesChange={() => setValuesChanged(true)}
              />

              <ConfirmModal
                open={leaveThePage}
                title="Tem certeza de que deseja sair sem gravar as alterações?"
                content="Toda a informação não gravada será perdida."
                okText="Sair"
                cancelText="Voltar"
                onOk={() => {
                  onCloseServiceDrawerForm();
                  setLeaveThePage(false);
                }}
                onCancel={() => setLeaveThePage(false)}
              ></ConfirmModal>
            </>
          }
          onClose={onLeaveThePage}
          open={openServiceDrawerForm}
          footerStyle={{ borderTop: "none" }}
          bodyStyle={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
          extra={
            <Button
              type="text"
              shape="circle"
              size="middle"
              icon={<CloseOutlined />}
              onClick={onLeaveThePage}
            />
          }
        />

        <ConfirmModal
          open={!!removeService}
          title={`Tem certeza que deseja apagar o serviço "${removeService?.name}"?`}
          okText="Apagar"
          cancelText="Voltar"
          onOk={() => removeServiceMutate(removeService?.id)}
          onCancel={() => setRemoveService(undefined)}
        ></ConfirmModal>
      </Card>
    </>
  );
}
